import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight, FiQuote, FiStar } = FiIcons;

const StoriesOfChange = () => {
  const [currentStory, setCurrentStory] = useState(0);

  const stories = [
    {
      name: 'Ramesh Kumar',
      age: 42,
      beforeImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      afterImage: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      beforeStory: 'Begged at traffic signals for 10 years after losing his job',
      afterStory: 'Now owns a successful tea stall earning ₹15,000/month',
      quote: 'I never imagined I could run my own business. This foundation gave me hope, skills, and most importantly - my dignity back.',
      location: 'New Delhi',
      timeframe: '8 months rehabilitation',
      impact: 'Supporting family of 4',
      skills: ['Business Management', 'Food Safety', 'Customer Service']
    },
    {
      name: 'Sunita Devi',
      age: 35,
      beforeImage: 'https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      afterImage: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      beforeStory: 'Single mother begging with 2 children at railway stations',
      afterStory: 'Works as trained cook in restaurant, children in school',
      quote: 'My children go to school now. We have a home, and I have dignity. I cook with pride, not desperation.',
      location: 'Mumbai',
      timeframe: '6 months rehabilitation',
      impact: 'Educating 2 children',
      skills: ['Professional Cooking', 'Food Hygiene', 'Kitchen Management']
    },
    {
      name: 'Arjun Singh',
      age: 38,
      beforeImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      afterImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      beforeStory: 'Homeless for 5 years after factory closure, lived on streets',
      afterStory: 'Manages organic farming project, earning ₹20,000/month',
      quote: 'From the streets to leading a farming initiative - this is what second chances look like. I grow food that feeds families.',
      location: 'Pune',
      timeframe: '10 months rehabilitation',
      impact: 'Leading team of 8 farmers',
      skills: ['Organic Farming', 'Team Leadership', 'Agricultural Technology']
    }
  ];

  const nextStory = () => {
    setCurrentStory((prev) => (prev + 1) % stories.length);
  };

  const prevStory = () => {
    setCurrentStory((prev) => (prev - 1 + stories.length) % stories.length);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiStar} className="w-4 h-4" />
            <span>Real Stories, Real Impact</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Stories of
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
              Transformation
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            These aren't just statistics. They're real people who found hope, dignity, and independence through your support.
          </p>
        </motion.div>

        {/* Stories Carousel */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStory}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left - Images */}
                <div className="relative">
                  <div className="grid grid-cols-2 h-full">
                    {/* Before Image */}
                    <div className="relative">
                      <img
                        src={stories[currentStory].beforeImage}
                        alt="Before transformation"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium mb-2">
                          Before
                        </div>
                        <div className="text-white text-sm font-medium">
                          {stories[currentStory].beforeStory}
                        </div>
                      </div>
                    </div>
                    
                    {/* After Image */}
                    <div className="relative">
                      <img
                        src={stories[currentStory].afterImage}
                        alt="After transformation"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium mb-2">
                          After
                        </div>
                        <div className="text-white text-sm font-medium">
                          {stories[currentStory].afterStory}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Transformation Arrow */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg">
                    <div className="text-2xl">→</div>
                  </div>
                </div>

                {/* Right - Story Content */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  {/* Name and Details */}
                  <div className="mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      {stories[currentStory].name}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                      <span>Age: {stories[currentStory].age}</span>
                      <span>•</span>
                      <span>{stories[currentStory].location}</span>
                      <span>•</span>
                      <span>{stories[currentStory].timeframe}</span>
                    </div>
                    
                    {/* Impact Badge */}
                    <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      <SafeIcon icon={FiStar} className="w-4 h-4" />
                      <span>{stories[currentStory].impact}</span>
                    </div>
                  </div>

                  {/* Quote */}
                  <blockquote className="mb-8">
                    <SafeIcon icon={FiQuote} className="w-8 h-8 text-orange-400 mb-4" />
                    <p className="text-lg text-gray-700 italic leading-relaxed mb-4">
                      "{stories[currentStory].quote}"
                    </p>
                  </blockquote>

                  {/* Skills Gained */}
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Skills Gained:</h4>
                    <div className="flex flex-wrap gap-2">
                      {stories[currentStory].skills.map((skill, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Call to Action */}
                  <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-6 text-white text-center">
                    <div className="text-lg font-bold mb-2">Your ₹500 Can Create the Next Story</div>
                    <div className="text-sm text-orange-100 mb-4">
                      Help someone else find their path from streets to success
                    </div>
                    <button className="bg-white text-orange-600 px-6 py-2 rounded-full font-bold hover:bg-gray-100 transition-colors duration-200">
                      Donate Now
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <button
            onClick={prevStory}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white shadow-2xl rounded-full p-4 hover:bg-gray-50 transition-colors duration-200 z-10"
          >
            <SafeIcon icon={FiChevronLeft} className="w-6 h-6 text-gray-700" />
          </button>
          
          <button
            onClick={nextStory}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white shadow-2xl rounded-full p-4 hover:bg-gray-50 transition-colors duration-200 z-10"
          >
            <SafeIcon icon={FiChevronRight} className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        {/* Story Indicators */}
        <div className="flex justify-center mt-8 space-x-3">
          {stories.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStory(index)}
              className={`w-4 h-4 rounded-full transition-all duration-200 ${
                index === currentStory
                  ? 'bg-orange-500 scale-110'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20"
        >
          <div className="text-center bg-white rounded-xl p-6 shadow-lg">
            <div className="text-3xl font-bold text-green-600 mb-2">2,547+</div>
            <div className="text-gray-600">Success Stories Like These</div>
          </div>
          <div className="text-center bg-white rounded-xl p-6 shadow-lg">
            <div className="text-3xl font-bold text-blue-600 mb-2">95%</div>
            <div className="text-gray-600">Success Rate in Our Program</div>
          </div>
          <div className="text-center bg-white rounded-xl p-6 shadow-lg">
            <div className="text-3xl font-bold text-orange-600 mb-2">₹500</div>
            <div className="text-gray-600">Can Start Someone's Journey</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StoriesOfChange;